import SEO from '../components/SEO';
import { offsiteEventsSchema, organizationSchema, breadcrumbSchema } from '../utils/structuredData';
import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Users, TrendingUp, CheckCircle, ArrowRight, Calendar, DollarSign } from 'lucide-react';

const OffsiteEventsPage = () => {
  return (
    <div style={styles.page}>
      <SEO
        title="Offsite Sales Events for Dealerships — Drive Massive Traffic & Sales"
        description="Driven2WIN produces high-impact offsite sales events at retail-traffic locations for car, golf cart, motorcycle, ATV, and RV dealers. Turnkey execution, permit handling, and guaranteed results. Average 85+ units sold per event."
        keywords="offsite sales events dealerships, automotive offsite event, car dealer offsite promotion, golf cart offsite sales event, motorcycle dealer offsite event, retail location sales event, high traffic dealership event"
        canonicalUrl="https://driven2win.com/services/offsite-events"
        structuredData={[offsiteEventsSchema, organizationSchema, breadcrumbSchema([{name:'Home',url:'https://driven2win.com'},{name:'Services',url:'https://driven2win.com/services'},{name:'Offsite Events',url:'https://driven2win.com/services/offsite-events'}])]}
      />
      <style>{`
        /* ── Offsite Events Page — Mobile Responsive ── */

        /* Hero */
        .oe-hero-h1 {
          font-size: clamp(1.75rem, 7vw, 4.5rem) !important;
          letter-spacing: 1px !important;
          word-break: break-word !important;
        }
        .oe-hero-sub {
          font-size: clamp(0.9rem, 3.5vw, 1.5rem) !important;
        }

        @media (max-width: 768px) {
          /* Sections */
          .oe-section { padding: clamp(2.5rem, 6vw, 4rem) 0 !important; }

          /* Headings */
          .oe-h2 { font-size: clamp(1.4rem, 5.5vw, 3rem) !important; letter-spacing: 0.5px !important; word-break: break-word !important; }
          .oe-cta-h2 { font-size: clamp(1.4rem, 5.5vw, 3.5rem) !important; }
          .oe-cta-text { font-size: 15px !important; }

          /* Overview grid — fixed 2-col MUST collapse to 1 col */
          .oe-overview-grid {
            grid-template-columns: 1fr !important;
            gap: 32px !important;
          }

          /* Stats box padding */
          .oe-stats-box { padding: 28px 20px !important; }
          .oe-stat-number { font-size: 40px !important; }
          .oe-stat-item { margin-bottom: 24px !important; }

          /* Process, benefits, ideal grids */
          .oe-process-grid { grid-template-columns: 1fr !important; gap: 32px !important; }
          .oe-benefits-grid { grid-template-columns: 1fr !important; gap: 20px !important; }
          .oe-ideal-grid { grid-template-columns: 1fr !important; gap: 16px !important; }

          /* Cards */
          .oe-process-step { padding: 36px 20px 24px !important; }
          .oe-benefit-card { padding: 28px 20px !important; }
          .oe-ideal-card { padding: 24px 20px !important; }

          /* CTA button */
          .oe-btn {
            font-size: 16px !important;
            padding: 14px 20px !important;
            white-space: normal !important;
            text-align: center !important;
            line-height: 1.3 !important;
          }

          /* Body text */
          .oe-body { font-size: 15px !important; }
        }

        @media (max-width: 400px) {
          .oe-h2 { font-size: 1.3rem !important; }
          .oe-stat-number { font-size: 34px !important; }
        }
      `}</style>
      {/* Hero */}
      <section style={styles.hero}>
        <div style={styles.heroOverlay} />
        <div style={styles.container}>
          <h1 style={styles.heroH1} className="oe-hero-h1">
            OFFSITE SALES <span style={{color: '#FFC300'}}>EVENTS</span>
          </h1>
          <p style={styles.heroSubheadline} className="oe-hero-sub">
            Take Your Dealership Where The Buyers Are — HIGH-TRAFFIC Locations That Generate MASSIVE Sales Volume
          </p>
        </div>
      </section>

      {/* Overview */}
      <section style={styles.overviewSection} className="oe-section">
        <div style={styles.container}>
          <div className="oe-overview-grid" style={styles.overviewGrid}>
            <div style={styles.overviewText}>
              <h2 style={styles.sectionH2} className="oe-h2">
                Don't Wait For Customers — <span style={{color: '#E31E24'}}>GO TO THEM!</span>
              </h2>
              <p style={styles.bodyText} className="oe-body">
                Why limit yourself to foot traffic at your dealership when you can set up shop at premium locations with THOUSANDS of qualified buyers walking by every hour?
              </p>
              <p style={styles.bodyText}>
                Our offsite sales events put your inventory in front of massive crowds at shopping centers, fairgrounds, parking lots, and high-traffic venues. We handle ALL the logistics — from venue selection to permits to setup — while you focus on closing deals.
              </p>
              <p style={styles.bodyText}>
                <strong>Result?</strong> More eyeballs, more test drives, more deals closed. It's that simple.
              </p>
            </div>
            
            <div style={styles.statsBox} className="oe-stats-box">
              <div style={styles.statItem} className="oe-stat-item">
                <div style={styles.statNumber} className="oe-stat-number">6,500+</div>
                <div style={styles.statLabel}>Events Produced</div>
              </div>
              <div style={styles.statItem}>
                <div style={styles.statNumber}>$300K+</div>
                <div style={styles.statLabel}>Avg Event Gross</div>
              </div>
              <div style={styles.statItem}>
                <div style={styles.statNumber}>50</div>
                <div style={styles.statLabel}>States Served</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section style={styles.howSection} className="oe-section">
        <div style={styles.container}>
          <h2 style={styles.sectionH2} className="oe-h2">
            HOW IT <span style={{color: '#E31E24'}}>WORKS</span>
          </h2>
          
          <div className="oe-process-grid" style={styles.processGrid}>
            <ProcessStep
              number="1"
              title="Venue Selection"
              description="We identify premium high-traffic locations in your target market — shopping centers, fairgrounds, parking lots with 10K+ daily visitors."
              icon={<MapPin />}
            />
            <ProcessStep
              number="2"
              title="Complete Planning"
              description="Our team handles ALL logistics: permits, insurance, signage, tents, power, lighting. You don't lift a finger."
              icon={<CheckCircle />}
            />
            <ProcessStep
              number="3"
              title="Aggressive Promotion"
              description="Multi-channel campaigns drive traffic: direct mail, Facebook, Instagram, SMS, email. We create BUZZ that fills your event."
              icon={<Users />}
            />
            <ProcessStep
              number="4"
              title="Event Execution"
              description="Professional setup, on-site support, and dealership branding that screams 'BIG EVENT!' Customers flood in ready to buy."
              icon={<TrendingUp />}
            />
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section style={styles.benefitsSection} className="oe-section">
        <div style={styles.container}>
          <h2 style={styles.sectionH2} className="oe-h2">
            WHY OFFSITE EVENTS <span style={{color: '#E31E24'}}>CRUSH IT</span>
          </h2>
          
          <div className="oe-benefits-grid" style={styles.benefitsGrid}>
            <BenefitCard
              icon={<Users />}
              title="Massive Foot Traffic"
              description="Tap into venues with 10K-50K+ daily visitors. Your inventory gets seen by THOUSANDS of qualified buyers."
            />
            <BenefitCard
              icon={<MapPin />}
              title="Geographic Expansion"
              description="Reach markets outside your normal radius. Attract buyers who would NEVER visit your physical location."
            />
            <BenefitCard
              icon={<TrendingUp />}
              title="Urgency & Excitement"
              description="Limited-time events create FOMO (fear of missing out). Buyers show up ready to deal NOW, not next week."
            />
            <BenefitCard
              icon={<DollarSign />}
              title="Proven ROI"
              description="Our events average $300K+ gross profit. Dealers consistently achieve 50-120+ unit sales per event."
            />
          </div>
        </div>
      </section>

      {/* Ideal For */}
      <section style={styles.idealSection} className="oe-section">
        <div style={styles.container}>
          <h2 style={styles.sectionH2} className="oe-h2">
            PERFECT FOR <span style={{color: '#E31E24'}}>THESE DEALERS</span>
          </h2>
          
          <div style={styles.idealGrid} className="oe-ideal-grid">
            <div style={styles.idealCard}>
              <h3 style={styles.idealTitle}>Car & Truck Dealerships</h3>
              <p style={styles.idealText}>New and used vehicle dealers looking to move aged inventory or launch new models with maximum impact.</p>
            </div>
            <div style={styles.idealCard}>
              <h3 style={styles.idealTitle}>Golf Cart Dealers</h3>
              <p style={styles.idealText}>Reach retirement communities, golf resorts, and high-income markets where golf carts are lifestyle vehicles.</p>
            </div>
            <div style={styles.idealCard}>
              <h3 style={styles.idealTitle}>Motorcycle & ATV Dealers</h3>
              <p style={styles.idealText}>Set up at motorsports events, rallies, and outdoor venues where enthusiasts gather in huge numbers.</p>
            </div>
            <div style={styles.idealCard}>
              <h3 style={styles.idealTitle}>RV Dealerships</h3>
              <p style={styles.idealText}>Position RVs at fairgrounds, expos, and outdoor festivals where adventure-seekers are ready to buy.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={styles.ctaSection} className="oe-section">
        <div style={styles.container}>
          <h2 style={styles.ctaH2} className="oe-cta-h2">
            Ready to Take Your Dealership <span style={{color: '#FFC300'}}>ON THE ROAD?</span>
          </h2>
          <p style={styles.ctaText} className="oe-cta-text">
            Let's create an offsite event that packs the lot and moves serious inventory!
          </p>
          <Link to="/contact#contact-form" style={styles.btnPrimary} className="oe-btn">
            <Calendar size={24} style={{marginRight: '12px'}} />
            Schedule Your FREE Event Strategy Call
          </Link>
        </div>
      </section>
    </div>
  );
};

// Components
const ProcessStep = ({ number, title, description, icon }) => (
  <div style={styles.processStep}>
    <div style={styles.processNumber}>{number}</div>
    <div style={styles.processIcon}>
      {React.cloneElement(icon, { size: 40, color: '#E31E24' })}
    </div>
    <h3 style={styles.processTitle}>{title}</h3>
    <p style={styles.processDescription}>{description}</p>
  </div>
);

const BenefitCard = ({ icon, title, description }) => (
  <div style={styles.benefitCard}>
    <div style={styles.benefitIcon}>
      {React.cloneElement(icon, { size: 48, color: 'white' })}
    </div>
    <h3 style={styles.benefitTitle}>{title}</h3>
    <p style={styles.benefitDescription}>{description}</p>
  </div>
);

// Styles
const styles = {
  page: {
    paddingTop: '109px',
  },
  container: {
    maxWidth: '1280px',
    margin: '0 auto',
    padding: '0 16px', // Mobile-first
  },
  hero: {
    position: 'relative',
    background: 'linear-gradient(135deg, #E31E24 0%, #FF6B35 100%)',
    padding: '100px 0',
    textAlign: 'center',
    overflow: 'hidden',
  },
  heroOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  heroH1: {
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '72px',
    color: '#FFFFFF',
    marginBottom: '24px',
    letterSpacing: '3px',
    position: 'relative',
    zIndex: 1,
  },
  heroSubheadline: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '24px',
    color: '#FFFFFF',
    maxWidth: '900px',
    margin: '0 auto',
    lineHeight: '1.6',
    position: 'relative',
    zIndex: 1,
    fontWeight: '600',
  },
  overviewSection: {
    padding: '100px 0',
    background: '#FFFFFF',
  },
  overviewGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(320px, 100%), 1fr))',
    gap: '48px',
    alignItems: 'center',
  },
  overviewText: {},
  sectionH2: {
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '48px',
    color: '#1A1F3A',
    marginBottom: '32px',
    letterSpacing: '1px',
  },
  bodyText: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '18px',
    lineHeight: '1.8',
    color: '#333',
    marginBottom: '20px',
  },
  statsBox: {
    background: 'linear-gradient(135deg, #1A1F3A 0%, #2C2C2C 100%)',
    borderRadius: '20px',
    padding: '48px 40px',
  },
  statItem: {
    marginBottom: '40px',
    textAlign: 'center',
  },
  statNumber: {
    fontFamily: 'Rajdhani, sans-serif',
    fontSize: '56px',
    fontWeight: '700',
    color: '#FFC300',
    marginBottom: '8px',
  },
  statLabel: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '14px',
    color: '#FFFFFF',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    fontWeight: '600',
  },
  howSection: {
    padding: '100px 0',
    background: '#F5F5F5',
  },
  processGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(250px, 100%), 1fr))',
    gap: '40px',
  },
  processStep: {
    position: 'relative',
    background: '#FFFFFF',
    borderRadius: '16px',
    padding: '40px 32px',
    textAlign: 'center',
    border: '3px solid #E5E5E5',
  },
  processNumber: {
    position: 'absolute',
    top: '-20px',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #E31E24 0%, #FF6B35 100%)',
    color: '#FFFFFF',
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '28px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 4px 16px rgba(227, 30, 36, 0.4)',
  },
  processIcon: {
    marginTop: '20px',
    marginBottom: '20px',
  },
  processTitle: {
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '28px',
    color: '#1A1F3A',
    marginBottom: '12px',
    letterSpacing: '1px',
  },
  processDescription: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '15px',
    lineHeight: '1.7',
    color: '#666',
  },
  benefitsSection: {
    padding: '100px 0',
    background: '#FFFFFF',
  },
  benefitsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(280px, 100%), 1fr))',
    gap: '32px',
  },
  benefitCard: {
    background: 'linear-gradient(135deg, #E31E24 0%, #FF6B35 100%)',
    borderRadius: '16px',
    padding: '40px 32px',
    textAlign: 'center',
    color: '#FFFFFF',
  },
  benefitIcon: {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    background: 'rgba(255, 255, 255, 0.2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 24px',
  },
  benefitTitle: {
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '28px',
    marginBottom: '12px',
    letterSpacing: '1px',
  },
  benefitDescription: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '15px',
    lineHeight: '1.7',
  },
  idealSection: {
    padding: '100px 0',
    background: '#F5F5F5',
  },
  idealGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(280px, 100%), 1fr))',
    gap: '32px',
  },
  idealCard: {
    background: '#FFFFFF',
    borderRadius: '16px',
    padding: '40px 32px',
    border: '3px solid #E5E5E5',
  },
  idealTitle: {
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '28px',
    color: '#E31E24',
    marginBottom: '16px',
    letterSpacing: '1px',
  },
  idealText: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '15px',
    lineHeight: '1.7',
    color: '#666',
  },
  ctaSection: {
    background: 'linear-gradient(135deg, #1A1F3A 0%, #2C2C2C 100%)',
    padding: '100px 0',
    textAlign: 'center',
  },
  ctaH2: {
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '56px',
    color: '#FFFFFF',
    marginBottom: '24px',
    letterSpacing: '2px',
  },
  ctaText: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '22px',
    color: '#E5E5E5',
    marginBottom: '48px',
    fontWeight: '600',
  },
  btnPrimary: {
    display: 'inline-flex',
    alignItems: 'center',
    background: 'linear-gradient(135deg, #E31E24 0%, #FF6B35 100%)',
    color: '#FFFFFF',
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '24px',
    padding: '20px 48px',
    border: 'none',
    borderRadius: '8px',
    textDecoration: 'none',
    boxShadow: '0 6px 24px rgba(227, 30, 36, 0.4)',
    transition: 'all 0.3s ease',
    letterSpacing: '1px',
  },
};

export default OffsiteEventsPage;
